import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Modal } from '@/components';
import { ModalProps } from '@mui/base/Modal';
import { IHarborData } from '@/types/harbor-management.types';
import clsx from 'clsx';
import { I18N_LANGUAGES } from '@/i18n';
import { useTranslateHarborContentMutation } from '@/services/HarborManagementService';
import { useSnackbar } from 'notistack';
import { useIntl } from 'react-intl';
import { Editor } from '@tinymce/tinymce-react';
import { tinymceInit, TINYMCE_SCRIPT_SRC } from '@/constant';
import DOMPurify from 'dompurify';
import { formatFinnishCoordinate } from '@/utils';

export interface HarborPreviewModalProps extends Omit<ModalProps, 'children'> {
  formData: Partial<IHarborData>;
  source_language?: string;
  /** Pre-existing translations from the API (edit mode). When provided the
   *  modal skips the auto-fetch and uses these directly. */
  initialTranslations?: Record<string, Partial<IHarborData>>;
  onTranslationsGenerated?: (
    translations: Record<string, Partial<IHarborData>>
  ) => void;
}

export interface LanguagePreviewProps {
  language: {
    code: string;
    label: string;
    flag: string;
  };
  formData: Partial<IHarborData>;
  translatedContent?: Record<
    string,
    {
      description?: string;
    }
  >;
  isLoading: boolean;
  onEditContent?: (
    languageCode: string,
    field: keyof EditValues,
    value: string
  ) => void;
}

// Interface for translation payload
interface TranslationPayload {
  description?: string;
  source_language: string;
}

// Interface for edit values
interface EditValues {
  description?: string;
}

export const LanguagePreview: React.FC<LanguagePreviewProps> = ({
  language,
  formData,
  translatedContent,
  isLoading,
  onEditContent,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValues, setEditValues] = useState<EditValues>({});

  const langTranslation = translatedContent?.[language.code];
  const langContent = React.useMemo(
    () => ({
      ...formData,
      description: langTranslation?.description ?? '',
    }),
    [formData, langTranslation?.description]
  );

  const intl = useIntl();

  const toggleEditMode = () => {
    if (!isEditing) {
      const initialValues: EditValues = {};
      if (langContent?.description)
        initialValues.description = langContent.description;
      setEditValues(initialValues);
    }
    setIsEditing(!isEditing);
  };

  const handleSave = () => {
    if (onEditContent) {
      Object.entries(editValues).forEach(([field, value]) => {
        onEditContent(language.code, field as keyof EditValues, value);
      });
      setIsEditing(false);
    }
  };

  const handleChange = (field: keyof EditValues, value: string) => {
    setEditValues((prev) => ({ ...prev, [field]: value }));
  };

  useEffect(() => {
    if (langContent) {
      const initialValues: EditValues = {};
      if (langContent.description)
        initialValues.description = langContent.description;
      setEditValues(initialValues);
    }
  }, [langContent]);

  return (
    <div className="bg-gray-100 shadow-sm hover:shadow-md transition-all duration-300 rounded-xl overflow-hidden mb-5 border border-gray-200">
      <div className="bg-gray-100 px-5 py-3 flex items-center justify-between border-b border-gray-100">
        <div className="flex items-center">
          <img
            src={language.flag}
            alt={language.label}
            className="w-6 h-6 mr-3 rounded-sm shadow-sm"
          />
          <h3 className="text-base font-medium text-gray-800 flex items-center">
            {language.label}
            {isLoading && (
              <div className="ml-2 w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
            )}
          </h3>
        </div>
        <div className="flex items-center gap-2">
          {isEditing ? (
            <>
              <button
                onClick={handleSave}
                className="px-3 py-1.5 text-xs font-medium text-green-700 dark:text-green-200 bg-green-100 dark:bg-green-900 rounded-lg hover:bg-green-200 dark:hover:bg-green-800 transition-colors duration-200"
                aria-label="Save changes"
              >
                {intl.formatMessage({ id: 'COMMON.SAVE' })}
              </button>
              <button
                onClick={() => setIsEditing(false)}
                className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors duration-200"
                aria-label="Cancel editing"
              >
                {intl.formatMessage({ id: 'COMMON.CANCEL' })}
              </button>
            </>
          ) : (
            <button
              onClick={toggleEditMode}
              className="px-3 py-1.5 text-xs font-medium text-blue-700 dark:text-blue-200 contrast:text-blue-100 bg-blue-100 dark:bg-blue-900 contrast:bg-blue-900 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-800 contrast:hover:bg-blue-800 transition-colors duration-200"
              aria-label="Edit content"
            >
              {intl.formatMessage({ id: 'COMMON.EDIT' })}
            </button>
          )}
        </div>
      </div>

      <div className="p-5">
        {/* Name Field */}
        <div className="mb-4">
          <span className="block text-sm font-medium text-gray-700 mb-2">
            {intl.formatMessage({ id: 'COMMON.NAME' })}
          </span>
          <p className="text-base font-medium text-gray-800 bg-gray-100 px-3 py-2 rounded-lg">
            {formData.name || 'Not provided'}
          </p>
        </div>

        {/* Description Field */}
        <div className="mb-4">
          <span className="block text-sm font-medium text-gray-700 mb-2">
            {intl.formatMessage({ id: 'COMMON.DESCRIPTION' })}
          </span>
          {isEditing ? (
            <Editor
              tinymceScriptSrc={TINYMCE_SCRIPT_SRC}
              value={editValues.description || ''}
              init={tinymceInit}
              onEditorChange={(content: string) =>
                handleChange('description', content)
              }
              textareaName="description"
            />
          ) : (
            <div
              className="text-sm bg-gray-100 px-3 py-2 rounded-lg min-h-[80px] text-gray-800 prose prose-strong:text-gray-800 prose-p:text-gray-800 prose-a:text-gray-800 prose-li:text-gray-800 prose-ul:text-gray-800 prose-ol:text-gray-800 prose-h1:text-gray-800 prose-h2:text-gray-800 prose-h3:text-gray-800 prose-h4:text-gray-800 prose-h5:text-gray-800 prose-h6:text-gray-800 max-w-none"
              dangerouslySetInnerHTML={{
                __html:
                  DOMPurify.sanitize(langContent?.description || '') ||
                  'Not provided',
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

const HarborPreviewModal: React.FC<HarborPreviewModalProps> = ({
  formData,
  source_language,
  open,
  onClose,
  className,
  initialTranslations,
  onTranslationsGenerated,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const intl = useIntl();
  const [translations, setTranslations] = useState<
    Record<string, Partial<IHarborData>>
  >(() => initialTranslations ?? {});
  const [isLoading, setIsLoading] = useState(false);

  // Sync whenever the parent updates pre-existing translations (e.g. language
  // switch or editor edit in HarborForm).
  useEffect(() => {
    if (initialTranslations && Object.keys(initialTranslations).length > 0) {
      setTranslations(initialTranslations);
    }
  }, [initialTranslations]);
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open && modalRef.current) {
      const timer = setTimeout(() => {
        modalRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [open]);

  const [translateHarborContent] = useTranslateHarborContentMutation();

  const handleEditContent = (
    languageCode: string,
    field: keyof EditValues,
    value: string
  ) => {
    setTranslations((prev) => {
      const nextTranslations = {
        ...prev,
        [languageCode]: {
          ...prev[languageCode],
          [field]: value,
        },
      };

      if (onTranslationsGenerated) {
        onTranslationsGenerated(nextTranslations);
      }

      return nextTranslations;
    });
  };

  const fetchTranslations = useCallback(async () => {
    if (!formData.description) {
      enqueueSnackbar('Please provide description to translate', {
        variant: 'warning',
      });
      return;
    }

    setIsLoading(true);
    try {
      const payload: TranslationPayload = {
        description: formData.description,
        source_language: source_language || 'en',
      };

      const result = await translateHarborContent(payload).unwrap();

      if (result.translations) {
        const formattedTranslations: Record<string, Partial<IHarborData>> = {};

        Object.entries(result.translations).forEach(
          ([langCode, translation]) => {
            formattedTranslations[langCode] = {
              description: translation.description,
            };
          }
        );

        setTranslations(formattedTranslations);

        if (onTranslationsGenerated) {
          onTranslationsGenerated(formattedTranslations);
        }
        if (result.warning_message) {
          enqueueSnackbar(result.warning_message, {
            variant: 'warning',
          });
        } else {
          enqueueSnackbar('Translations generated successfully', {
            variant: 'success',
          });
        }
      }
    } catch (error) {
      console.error('Translation error:', error);
      enqueueSnackbar('Failed to generate translations', {
        variant: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  }, [
    formData.description,
    source_language,
    enqueueSnackbar,
    translateHarborContent,
    onTranslationsGenerated,
  ]);

  useEffect(() => {
    // We want to auto-fetch if we basically don't have all languages yet.
    // If translations only has 1 key, it's because the user just typed in
    // the main editor for their current language in create mode.
    if (open && Object.keys(translations).length < I18N_LANGUAGES.length) {
      // Don't autofetch again if we're already loading or just errored previously,
      // but fetchTranslations handles its own debounce via isLoading in real life.
      fetchTranslations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, fetchTranslations]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      className={clsx(
        'harbor-preview-root !flex items-center justify-center',
        className
      )}
      aria-labelledby="harbor-preview-modal-title"
    >
      <div
        ref={modalRef}
        tabIndex={-1}
        className="harbor-preview-modal bg-gray-100 w-full max-w-4xl mx-4 rounded-xl shadow-xl flex flex-col overflow-hidden"
      >
        <div className="modal-header px-6 py-4 bg-gradient-to-r from-blue-600 dark:from-blue-900 to-blue-700 dark:to-blue-950 border-b border-blue-200">
          <div className="modal-header-row flex items-center justify-between">
            <div>
              <h1
                id="harbor-preview-modal-title"
                className="text-xl font-semibold text-white"
              >
                {intl.formatMessage({ id: 'HARBOR.PREVIEW.TITLE' })}
              </h1>
              <p className="text-blue-100 text-sm mt-1">
                {intl.formatMessage({ id: 'HARBOR.PREVIEW.DESCRIPTION' })}
              </p>
            </div>
          </div>
        </div>

        <div className="p-6">
          {/* Harbor Information Header */}
          <div className="bg-gray-100 rounded-lg p-4 mb-6">
            <div className="info-header-row flex items-start justify-between">
              <div className="flex-1">
                <h2 className="font-semibold text-gray-800 mb-2">
                  {formData.name || 'Unnamed Harbor'}
                </h2>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-gray-600">
                      {intl.formatMessage({ id: 'COMMON.CATEGORY' })}:
                    </span>
                    <span className="ml-2 text-gray-800">
                      {formData.category ||
                        intl.formatMessage({ id: 'COMMON.NOT_SPECIFIED' })}
                    </span>
                  </div>
                </div>
              </div>

              {/* Coordinates */}
              {formData.latitude && formData.longitude && (
                <div className="text-right">
                  <div className="flex items-center text-xs text-gray-500 mb-1">
                    <svg
                      className="w-4 h-4 mr-1"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                    {intl.formatMessage({ id: 'HARBOR.COORDINATES' })}
                  </div>
                  <div className="text-sm font-medium text-gray-800">
                    {formatFinnishCoordinate(Number(formData.latitude), {
                      minimumFractionDigits: 6,
                      maximumFractionDigits: 6,
                    })}
                    ,{' '}
                    {formatFinnishCoordinate(Number(formData.longitude), {
                      minimumFractionDigits: 6,
                      maximumFractionDigits: 6,
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>

          <p className="text-sm text-gray-500 mb-4 flex items-center">
            <svg
              className="w-4 h-4 mr-1"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            {intl.formatMessage({ id: 'HARBOR.PREVIEW.DESCRIPTION' })}
          </p>

          <div className="lang-list overflow-y-auto max-h-[50vh] pr-2 custom-scrollbar">
            {I18N_LANGUAGES.map((lang) => (
              <LanguagePreview
                key={lang.code}
                language={lang}
                formData={formData}
                translatedContent={translations}
                isLoading={isLoading}
                onEditContent={handleEditContent}
              />
            ))}
          </div>
        </div>

        <div className="px-6 py-4 bg-gray-100 border-t border-gray-100">
          <div className="footer-actions-row flex justify-between items-center">
            <button
              onClick={fetchTranslations}
              disabled={isLoading}
              className="inline-flex items-center px-4 py-2 bg-green-700 dark:bg-green-900 contrast:bg-green-900 text-white text-sm font-medium rounded-lg hover:bg-green-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 shadow-sm transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Regenerate translations"
            >
              {isLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  {intl.formatMessage({ id: 'COMMON.GENERATING' })}
                </>
              ) : (
                <>
                  <svg
                    className="w-4 h-4 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                  {intl.formatMessage({ id: 'COMMON.REGENERATE' })}
                </>
              )}
            </button>
            <button
              type="button"
              onClick={() => onClose?.({}, 'backdropClick')}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 shadow-sm transition-colors duration-200"
              aria-label="Close preview modal"
            >
              {intl.formatMessage({ id: 'COMMON.CLOSE' })}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default HarborPreviewModal;
